import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { PasswordInput } from '@/components/ui/password-input';

interface FormPasswordFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  label: string;
  placeholder?: string;
  description?: React.ReactNode;
  required?: boolean;
  /** Right-aligned link below the field (e.g. "Forgot password?"). */
  helper?: React.ReactNode;
  inputClassName?: string;
  className?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  showLockIcon?: boolean;
  showToggle?: boolean;
  autoComplete?: string;
}

export const FormPasswordField = <TFieldValues extends FieldValues>({
  control,
  name,
  label,
  placeholder = '••••••••',
  description,
  required,
  helper,
  inputClassName,
  className,
  autoFocus,
  disabled,
  showLockIcon,
  showToggle,
  autoComplete,
}: FormPasswordFieldProps<TFieldValues>) => (
  <FormField
    control={control}
    name={name}
    render={({ field }) => (
      <FormItem className={className}>
        <FormLabel>
          {label} {required && <span className="text-primary">*</span>}
        </FormLabel>
        <FormControl>
          <PasswordInput
            placeholder={placeholder}
            className={inputClassName}
            autoFocus={autoFocus}
            disabled={disabled}
            showLockIcon={showLockIcon}
            showToggle={showToggle}
            autoComplete={autoComplete}
            {...field}
          />
        </FormControl>
        {description && <FormDescription>{description}</FormDescription>}
        <FormMessage />
        {helper && <div className="flex justify-end">{helper}</div>}
      </FormItem>
    )}
  />
);
FormPasswordField.displayName = 'FormPasswordField';
